import {
  DEFAULT_DIFFICULTY,
  DEFAULT_FAIL_MODE,
  DEFAULT_MUSIC,
  DEFAULT_ROUND_SECONDS,
  DEFAULT_SOUND,
  DIFFICULTY_PROFILES,
  ROUND_DURATIONS,
  type Difficulty,
  type FailMode,
  type RoundDuration,
} from './config';

const STORAGE_KEY = 'pnp.settings.v2';

export interface Settings {
  sound: boolean;
  music: boolean;
  difficulty: Difficulty;
  roundSeconds: RoundDuration;
  failMode: FailMode;
}

export type PartialSettings = Partial<Settings>;

const DEFAULTS: Readonly<Settings> = Object.freeze({
  sound: DEFAULT_SOUND,
  music: DEFAULT_MUSIC,
  difficulty: DEFAULT_DIFFICULTY,
  roundSeconds: DEFAULT_ROUND_SECONDS,
  failMode: DEFAULT_FAIL_MODE,
});

const DIFFICULTIES = new Set<Difficulty>(Object.keys(DIFFICULTY_PROFILES) as Difficulty[]);
const FAIL_MODES = new Set<FailMode>(['none', 'points', 'time']);
const DURATIONS = new Set<number>(ROUND_DURATIONS);

function validate(partial: Record<string, unknown>): PartialSettings {
  const out: PartialSettings = {};
  if (typeof partial.sound === 'boolean') out.sound = partial.sound;
  if (typeof partial.music === 'boolean') out.music = partial.music;
  if (typeof partial.difficulty === 'string' && DIFFICULTIES.has(partial.difficulty as Difficulty)) {
    out.difficulty = partial.difficulty as Difficulty;
  }
  if (typeof partial.failMode === 'string' && FAIL_MODES.has(partial.failMode as FailMode)) {
    out.failMode = partial.failMode as FailMode;
  }
  if (typeof partial.roundSeconds === 'number' && DURATIONS.has(partial.roundSeconds)) {
    out.roundSeconds = partial.roundSeconds as RoundDuration;
  }
  return out;
}

function read(): PartialSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return validate(parsed as Record<string, unknown>);
    }
  } catch {
    // corrupt JSON or storage unavailable — fall through to defaults
  }
  return {};
}

function write(stored: PartialSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // storage full or unavailable — settings simply won't persist this session
  }
}

export function getSettings(): Settings {
  return { ...DEFAULTS, ...read() };
}

export function updateSettings(partial: PartialSettings): Settings {
  const stored = read();
  const next: PartialSettings = { ...stored, ...validate(partial as Record<string, unknown>) };
  write(next);
  return { ...DEFAULTS, ...next };
}

export function resetSettings(): Settings {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  return { ...DEFAULTS };
}
